import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button } from '@chatui/core'
import '@chatui/core/dist/index.css'
import QRCode from 'qrcode'
import { getWebSocketUrl } from '../config/environment'

interface LoginModalProps {
  visible: boolean
  onClose: () => void
  onLoginSuccess: (token: string) => void
}

interface QRCodeResponse {
  status: number
  msg: string
  data: {
    token: string
    qrcode_login_url: string
  }
}

interface WebSocketMessage {
  type: string
  message: string
  data?: {
    login_token: string
  }
}

const LoginModal: React.FC<LoginModalProps> = ({ visible, onClose, onLoginSuccess }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [qrToken, setQrToken] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)

  // 生成二维码图片URL
  const generateQRCodeImage = async (url: string): Promise<string> => {
    try {
      return await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      })
    } catch (error) {
      console.error('二维码生成失败:', error)
      throw error
    }
  }

  // 连接WebSocket检查登录状态
  const connectWebSocket = (token: string) => {
    if (wsRef.current) {
      wsRef.current.close()
    }

    const wsUrl = `${getWebSocketUrl()}qrcode-login/${token}`
    console.log('连接WebSocket:', wsUrl)
    
    wsRef.current = new WebSocket(wsUrl)
    
    wsRef.current.onopen = () => {
      console.log('WebSocket连接已建立')
      setChecking(true)
    }
    
    wsRef.current.onmessage = (event) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data)
        console.log('收到WebSocket消息:', data)
        
        if (data.type === 'connection_established') {
          console.log('二维码登录WebSocket连接成功')
          // 这里可以添加连接成功的处理逻辑
        } else if (data.type === 'login_success' && data.data) {
          // 登录成功
          console.log('登录成功，token:', data.data.login_token)
          localStorage.setItem('token', data.data.login_token)
          onLoginSuccess(data.data.login_token)
          onClose()
        }
      } catch (error) {
        console.error('解析WebSocket消息失败:', error)
      }
    }
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket连接错误:', error)
      setChecking(false)
    }
    
    wsRef.current.onclose = () => {
      console.log('WebSocket连接已关闭')
      setChecking(false)
    }
  }

  // 获取二维码
  const getQRCode = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/v1/user/qrcode/login', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data: QRCodeResponse = await response.json()
        if (data.status === 0) {
          // 使用qrcode_login_url生成二维码图片
          try {
            const qrCodeImageUrl = await generateQRCodeImage(data.data.qrcode_login_url)
            setQrCodeUrl(qrCodeImageUrl)
            setQrToken(data.data.token)
            console.log('二维码获取成功')
            
            // 连接WebSocket检查登录状态
            connectWebSocket(data.data.token)
          } catch (error) {
            console.error('二维码生成失败:', error)
          }
        } else {
          console.error(data.msg || '获取二维码失败')
        }
      } else {
        console.error('网络请求失败')
      }
    } catch (error) {
      console.error('获取二维码失败:', error)
      console.error('获取二维码失败')
    } finally {
      setLoading(false)
    }
  }

  // 清理WebSocket连接
  const cleanupWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }

  // 当模态框显示时获取二维码
  useEffect(() => {
    if (visible) {
      getQRCode()
    } else {
      cleanupWebSocket()
    }
  }, [visible])

  // 组件卸载时清理WebSocket
  useEffect(() => {
    return () => {
      cleanupWebSocket()
    }
  }, [])

  if (!visible) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '400px',
        width: '90%',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #e8e8e8',
          paddingBottom: '10px'
        }}>
          <h3 style={{ margin: 0 }}>扫码登录</h3>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#999'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center'
        }}>
          {loading ? (
            <div style={{ padding: '40px 20px' }}>
              <div style={{ fontSize: '16px', color: '#666' }}>正在生成二维码...</div>
            </div>
          ) : qrCodeUrl ? (
            <>
              <div style={{ 
                width: '200px', 
                height: '200px', 
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                backgroundColor: '#fafafa'
              }}>
                <img 
                  src={qrCodeUrl} 
                  alt="登录二维码" 
                  style={{ 
                    width: '180px', 
                    height: '180px',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                请使用微信扫描二维码登录
              </div>
              {checking && (
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
                  正在监听登录状态...
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <Button 
                  size="sm" 
                  onClick={getQRCode}
                  disabled={loading}
                >
                  刷新二维码
                </Button>
                <Button 
                  size="sm" 
                  color="primary" 
                  onClick={onClose}
                >
                  取消
                </Button>
              </div>
            </>
          ) : (
            <div style={{ padding: '40px 20px' }}>
              <div style={{ fontSize: '16px', color: '#666' }}>二维码加载失败</div>
              <Button 
                size="sm" 
                onClick={getQRCode}
                style={{ marginTop: '10px' }}
              >
                重试
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginModal 